import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import Cadence, { CadenceType } from '@models/Cadence';
import { Select } from '@components/FormInputs';
import { capitalize } from '@support/string';
import styles from './Reconnect.module.scss';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { ordinalize } from '@support/number';

// Component for editing an existing cadence (by type) and Component for displaying cadence preview

interface EditorProps {
  cadence: Cadence;
  setCadence: Dispatch<Cadence | null>;
  className?: string;
}

type Option = { value: any; label: any };

const mapFromOptions = (opts: Array<Option>): { [key: string]: Option } => {
  return opts.reduce((optsMap, opt) => ({ ...optsMap, [opt.value]: opt }), {});
};

const qualifierOptions = ['first', 'second', 'third', 'fourth', 'last'].map((value) => ({
  value,
  label: capitalize(value),
}));
const qualifierOptionsMap = mapFromOptions(qualifierOptions);

// We display Monday as the first option b/c unlikely a user will want
// to be reminded on Sunday/Saturday
const dayOfWeekOptions = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
  'Day',
].map((label, idx) => ({ label, value: idx == 7 ? null : idx }));
const dayOfWeekOptionsMap = mapFromOptions(dayOfWeekOptions);

const monthOptions = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
].map((label, value) => ({ label, value: value + 1 }));
const monthOptionsMap = mapFromOptions(monthOptions);

// No need for a map of these b/c we can just use index to get an option
const dayOfMonthOptions = (cadence: Cadence) => {
  const year = new Date().getFullYear();
  const daysInMonth = new Date(year, cadence.month, 0).getDate();
  return Array.from({ length: daysInMonth }, (_, i) => ({ value: i + 1, label: i + 1 }));
};

const ordinalizedDayOptions = Array.from({ length: 31 }, (_, i) => ({
  value: i + 1,
  label: ordinalize(i + 1),
}));

const QualifierSelect = ({ cadence, setCadence }: EditorProps) => (
  <Select
    options={qualifierOptions}
    defaultValue={qualifierOptionsMap[cadence.qualifier]}
    onChange={({ value }) => setCadence(cadence.copyWith({ qualifier: value }))}
    isInline
    isSearchable
  />
);

const DayOfWeekSelect = ({ cadence, setCadence }: EditorProps) => {
  const options = useMemo(() => {
    if (cadence.type == 'weekly') {
      return dayOfWeekOptions.slice(0, 7);
    }
    return dayOfWeekOptions;
  }, [cadence.type]);

  const onChange = ({ value }) => {
    const newProps: { dayOfWeek?: number; dayOfMonth?: number } = { dayOfWeek: value };
    if (value === null) {
      newProps.dayOfMonth = cadence.dayOfMonth || 1;
    }
    setCadence(cadence.copyWith(newProps));
  };

  return (
    <Select
      options={options}
      defaultValue={dayOfWeekOptionsMap[cadence.dayOfWeek]}
      onChange={onChange}
      isInline
      isSearchable
    />
  );
};

const MonthSelect = ({ cadence, setCadence }: EditorProps) => (
  <Select
    options={monthOptions}
    defaultValue={monthOptionsMap[cadence.month]}
    onChange={({ value }) => setCadence(cadence.copyWith({ month: value }))}
    isInline
    isSearchable
  />
);

const DayOfMonthSelect = ({ cadence, setCadence }: EditorProps) => {
  const [options, setOptions] = useState(dayOfMonthOptions(cadence));

  useEffect(() => {
    const newOptions = dayOfMonthOptions(cadence);

    if (newOptions.length != options.length) {
      setOptions(newOptions);
    }

    // If user switches to a month with fewer days than the current
    // value of cadence.dayOfMonth, update to the last day of the newly
    // selected month
    if (cadence.dayOfMonth > newOptions.length) {
      setCadence(cadence.copyWith({ dayOfMonth: newOptions.length }));
    }
  }, [cadence]);

  return (
    <Select
      // using the key here ensures we update the defaultValue when the effect hook fires
      key={cadence.dayOfMonth}
      options={options}
      defaultValue={options[cadence.dayOfMonth ? cadence.dayOfMonth - 1 : 0]}
      onChange={({ value }) => setCadence(cadence.copyWith({ dayOfMonth: value }))}
      isInline
      isSearchable
    />
  );
};

const OrdinalizedDaySelect = ({ cadence, setCadence }: EditorProps) => (
  <Select
    options={ordinalizedDayOptions}
    defaultValue={ordinalizedDayOptions[cadence.dayOfMonth ? cadence.dayOfMonth - 1 : 0]}
    onChange={({ value }) => setCadence(cadence.copyWith({ dayOfMonth: value }))}
    isInline
    isSearchable
  />
);

const autoPreviewText = 'Clay Intelligently Decides';
const nonePreviewText = (isSelected: boolean): string => (isSelected ? 'Disabled' : 'Turn Off');

const Editor = ({ children }) => <span className={styles.cadenceEditor}>{children}</span>;

// only for 'weekly' | 'monthly' | 'quarterly' | 'yearly'
export const CadenceEditor = ({ cadence, setCadence }: EditorProps) => {
  if (cadence.type == 'none') {
    return <Editor>{nonePreviewText(true)}</Editor>;
  } else if (cadence.type == 'auto') {
    return <Editor>{autoPreviewText}</Editor>;
  }

  let copy = `Every ${cadence.interval} on `;
  let editors = [DayOfWeekSelect];

  if (['monthly', 'quarterly'].includes(cadence.type)) {
    copy = copy + 'the ';

    if (cadence.dayOfWeek !== null) {
      editors = [QualifierSelect].concat(editors);
    } else {
      editors = [OrdinalizedDaySelect].concat(editors);
    }
  } else if (cadence.type == 'yearly') {
    editors = [MonthSelect, DayOfMonthSelect];
  }

  return (
    <Editor>
      {copy}
      {editors.map((EditorComponent, idx) => (
        <EditorComponent key={idx} {...{ cadence, setCadence }} />
      ))}
    </Editor>
  );
};

const Highlight = ({ children }) => (
  <SwitchTransition>
    <CSSTransition
      key={children}
      // weird issue with types... this matches the documentation
      // @ts-ignore
      addEndListener={(node, done) => node.addEventListener('transitionend', done, false)}
      classNames={{
        enter: styles.highlightEnter,
        enterActive: styles.highlightEnterActive,
        enterDone: styles.highlightEnterDone,
        exit: styles.highlightExit,
      }}
    >
      <span className={styles.highlight}>{children}</span>
    </CSSTransition>
  </SwitchTransition>
);

export const CadencePreview = ({ cadence, type }: { cadence: Cadence; type: CadenceType }) => {
  if (type == 'none') {
    return <>{nonePreviewText(false)}</>;
  } else if (type == 'auto') {
    return <>{autoPreviewText}</>;
  }

  if (!type || !cadence) {
    return null;
  }

  const previewCadence = cadence.copyWith({ type });
  const interval = `Every ${previewCadence.interval}`;
  const day = previewCadence.formattedDay;
  const time = previewCadence.formattedTime;

  return (
    <>
      <Highlight>{interval}</Highlight>
      &nbsp;on{['monthly', 'quarterly'].includes(previewCadence.type) && ' the'}&nbsp;
      <Highlight>{day}</Highlight>
      &nbsp;at&nbsp;
      <Highlight>{time}</Highlight>
    </>
  );
};
